// 2026-07-01: Phase 14b — Anodize Batches (NADCAP Chemical Processing — AC7108)
// Captures anodizing tank runs: process type/spec, bath params, seal, and the mandatory
// test-panel (coupon) result. Maps the customer's "Chemical Process — Anodizing" step.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { customerbasedshift, cleanCustomerId } from "../../Services/app/operatorservice";
import { shiftadd } from "../../Services/app/masterservice";
import Swal from 'sweetalert2';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button,
  TextField, MenuItem, Tooltip, IconButton, Chip,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import VerifiedIcon from '@mui/icons-material/Verified';
import ScienceIcon from '@mui/icons-material/Science';
import GavelIcon from '@mui/icons-material/Gavel';

const BR = {
  orange: '#f97316', blue: '#2563eb', green: '#16a34a', amber: '#f59e0b', red: '#dc2626',
  dark: '#111827', muted: '#6b7280', border: '#e5e7eb', bg: '#f9fafb',
};

// MIL-A-8625 / AMS2470-2472 process families
const PROCESS_TYPES = [
  'Type I — Chromic Acid (CAA)',
  'Type IB — Low-voltage Chromic',
  'Type IC — Non-chromic (boric-sulfuric BSAA)',
  'Type II — Sulfuric Acid (SAA)',
  'Type IIB — Thin-film sulfuric',
  'Type III — Hardcoat (HAA)',
];
const TANKS = ['PMI-ANO-T1', 'PMI-ANO-T2', 'PMI-ANO-T3', 'PMI-ANO-T4'];
const SEALS = ['Hot DI Water', 'Nickel Acetate', 'Dichromate', 'Sodium Dichromate', 'None (per spec)'];
const PANEL_RESULTS = ['PASS', 'FAIL', 'PENDING'];

const STATUS_INFO = {
  COMPLETE_OK:    { color: BR.green, bg: '#dcfce7', label: '✓ ACCEPTED', icon: <VerifiedIcon fontSize="small" /> },
  COMPLETE_HOLD:  { color: BR.amber, bg: '#fef3c7', label: '⚠ ON HOLD', icon: <GavelIcon fontSize="small" /> },
  RUNNING:        { color: BR.orange, bg: '#fff7ed', label: '▶ RUNNING', icon: <ScienceIcon fontSize="small" /> },
  REJECTED:       { color: BR.red, bg: '#fee2e2', label: '✗ REJECTED', icon: <GavelIcon fontSize="small" /> },
};

const PANEL_COLOR = { PASS: BR.green, FAIL: BR.red, PENDING: BR.muted };

function fmtDate(d) {
  if (!d) return '—';
  const dt = new Date(d);
  if (isNaN(dt.getTime())) return '—';
  return dt.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }) + ' ' +
         dt.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: false });
}

const emptyBatch = {
  id: '',
  batch_no: '',
  tank_id: '',
  process_type: '',
  spec: '',
  bath_temp_c: '',
  voltage_v: '',
  current_density_asf: '',
  immersion_min: '',
  seal_type: '',
  wo_numbers: [],
  qty_parts: '',
  test_panel_no: '',
  test_panel_result: 'PENDING',
  coating_weight_mgdm2: '',
  operator_name: '',
  start_ts: '',
  end_ts: '',
  qa_signoff_by: '',
  qa_signoff_ts: '',
  status: 'RUNNING',
  notes: '',
};

export default function AnodizeBatches() {
  const customerId = cleanCustomerId(localStorage.getItem('CustomerID') || window._env_.CUSTOMER_ID);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('ALL');
  const [search, setSearch] = useState('');
  const [editOpen, setEditOpen] = useState(false);
  const [draft, setDraft] = useState(emptyBatch);
  const [saving, setSaving] = useState(false);

  const refresh = useCallback(async () => {
    if (!customerId) return;
    setLoading(true);
    try {
      const r = await customerbasedshift(customerId, 'anodize_batches');
      const v = Array.isArray(r?.[0]?.value) ? r[0].value : [];
      setItems(v);
    } catch (e) { setItems([]); } finally { setLoading(false); }
  }, [customerId]);

  useEffect(() => { refresh(); }, [refresh]);

  const totals = useMemo(() => {
    const out = { ALL: items.length, RUNNING: 0, COMPLETE_OK: 0, COMPLETE_HOLD: 0, REJECTED: 0 };
    items.forEach(i => { out[i.status] = (out[i.status] || 0) + 1; });
    return out;
  }, [items]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items.filter(i => {
      if (filter !== 'ALL' && i.status !== filter) return false;
      if (!q) return true;
      return (i.batch_no || '').toLowerCase().includes(q)
          || (i.tank_id || '').toLowerCase().includes(q)
          || (i.process_type || '').toLowerCase().includes(q)
          || (i.spec || '').toLowerCase().includes(q)
          || (i.test_panel_no || '').toLowerCase().includes(q);
    }).sort((a, b) => (new Date(b.start_ts) - new Date(a.start_ts)));
  }, [items, filter, search]);

  function openAdd() { setDraft({ ...emptyBatch, id: `ano-${Date.now()}` }); setEditOpen(true); }
  function openEdit(item) { setDraft({ ...emptyBatch, ...item }); setEditOpen(true); }

  async function save() {
    if (!draft.batch_no || !draft.process_type) {
      Swal.fire({ icon: 'warning', title: 'Batch # and process type required', confirmButtonColor: BR.orange });
      return;
    }
    setSaving(true);
    try {
      const exists = items.findIndex(i => i.id === draft.id);
      const next = exists === -1 ? [...items, draft]
                                 : items.map((it, idx) => idx === exists ? draft : it);
      await shiftadd({ anodize_batches: next }, customerId, 'SERVER_SCOPE');
      setItems(next); setEditOpen(false);
      Swal.fire({ toast: true, position: 'top-end', timer: 1800, showConfirmButton: false,
                   icon: 'success', title: `${draft.batch_no} saved` });
    } catch (e) {
      Swal.fire({ icon: 'error', title: 'Save failed', text: e.message, confirmButtonColor: BR.orange });
    } finally { setSaving(false); }
  }

  async function signOff(item) {
    const panel = item.test_panel_result || 'PENDING';
    const panelWarn = panel !== 'PASS'
      ? `<div style="color:#b91c1c; font-weight:700; margin-top:6px;">⚠ Test panel is ${panel} — batch cannot be accepted until the coupon passes.</div>` : '';
    const { value: form } = await Swal.fire({
      title: `QA sign-off · ${item.batch_no}`,
      html: `
        <div style="text-align:left; font-size:13px;">
          <div style="background:#fff7ed; padding:10px; border-radius:6px; margin-bottom:12px;">
            <strong>Process:</strong> ${item.process_type || '?'}<br/>
            <strong>Spec:</strong> ${item.spec || 'NOT RECORDED'} · <strong>Tank:</strong> ${item.tank_id || '—'}<br/>
            <strong>Bath:</strong> ${item.bath_temp_c || '?'}°C · ${item.voltage_v || '?'}V · ${item.immersion_min || '?'} min<br/>
            <strong>Seal:</strong> ${item.seal_type || '—'}<br/>
            <strong>Test panel:</strong> ${item.test_panel_no || 'NONE'} → <strong>${panel}</strong>
            ${panelWarn}
          </div>
          <label style="font-weight:700; display:block; margin-bottom:6px;">Disposition</label>
          <select id="qa-disp" style="width:100%; padding:10px; font-size:14px; font-weight:600; border:1.5px solid #d1d5db; border-radius:6px;">
            <option value="COMPLETE_OK">✓ Accept — panel passed, within spec</option>
            <option value="COMPLETE_HOLD">⚠ Hold — needs review / retest</option>
            <option value="REJECTED">✗ Reject — out of spec / panel failed</option>
          </select>
          <label style="font-weight:700; display:block; margin-top:12px; margin-bottom:6px;">QA name</label>
          <input id="qa-name" placeholder="Your name (sign-off)" style="width:100%; padding:9px 12px; font-size:14px; border:1.5px solid #d1d5db; border-radius:6px;"/>
          <label style="font-weight:700; display:block; margin-top:12px; margin-bottom:6px;">Note</label>
          <textarea id="qa-note" rows="2" style="width:100%; padding:8px; font-size:13px; border:1.5px solid #d1d5db; border-radius:6px; font-family:inherit;"></textarea>
        </div>
      `,
      icon: 'info', showCancelButton: true,
      confirmButtonText: 'Sign off', confirmButtonColor: BR.orange,
      cancelButtonText: 'Cancel', reverseButtons: true,
      preConfirm: () => {
        const disp = document.getElementById('qa-disp').value;
        const name = document.getElementById('qa-name').value.trim();
        const note = document.getElementById('qa-note').value.trim();
        if (!name) { Swal.showValidationMessage('QA name required for sign-off'); return false; }
        if (disp === 'COMPLETE_OK' && panel !== 'PASS') {
          Swal.showValidationMessage('Cannot Accept while the test panel is not PASS'); return false;
        }
        return { disp, name, note };
      },
    });
    if (!form) return;
    const next = items.map(i => i.id === item.id ? {
      ...i, status: form.disp,
      qa_signoff_by: form.name,
      qa_signoff_ts: new Date().toISOString(),
      notes: form.note ? `${i.notes || ''}\nQA: ${form.note}` : i.notes,
    } : i);
    await shiftadd({ anodize_batches: next }, customerId, 'SERVER_SCOPE');
    setItems(next);
    Swal.fire({ toast: true, position: 'top-end', timer: 1800, showConfirmButton: false,
                 icon: 'success', title: `Signed off by ${form.name}` });
  }

  return (
    <div style={{ padding: 16, background: BR.bg, minHeight: '100vh' }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 14 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 900, color: BR.dark }}>
            🧪 Anodize Batches
          </h1>
          <div style={{ fontSize: 12, color: BR.muted, marginTop: 4 }}>
            Process type · bath params · seal · test panel (coupon) · QA sign-off · NADCAP AC7108 Chemical Processing
          </div>
        </div>
        <button onClick={openAdd}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 6,
                          padding: '9px 16px', background: BR.orange, color: '#fff',
                          border: 'none', borderRadius: 6, fontWeight: 800, fontSize: 13, cursor: 'pointer' }}>
          <AddIcon fontSize="small" /> Record Batch
        </button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 10, marginBottom: 14 }}>
        {[
          { key: 'ALL', label: 'All batches', color: BR.dark },
          { key: 'RUNNING', label: 'Running', color: BR.orange },
          { key: 'COMPLETE_OK', label: 'Accepted', color: BR.green },
          { key: 'COMPLETE_HOLD', label: 'On hold', color: BR.amber },
          { key: 'REJECTED', label: 'Rejected', color: BR.red },
        ].map(t => (
          <div key={t.key} onClick={() => setFilter(t.key)}
               style={{ background: '#fff', border: `2px solid ${filter === t.key ? t.color : BR.border}`,
                        borderRadius: 8, padding: '10px 12px', cursor: 'pointer' }}>
            <div style={{ fontSize: 10, fontWeight: 800, color: BR.muted, textTransform: 'uppercase', letterSpacing: 0.5 }}>{t.label}</div>
            <div style={{ fontSize: 24, fontWeight: 900, color: t.color, lineHeight: 1.1, marginTop: 4 }}>
              {totals[t.key] ?? 0}
            </div>
          </div>
        ))}
      </div>

      <input value={search} onChange={e => setSearch(e.target.value)}
             placeholder="Search batch #, tank, process, spec, panel #…"
             style={{ width: '100%', padding: '8px 12px', border: `1px solid ${BR.border}`,
                       borderRadius: 6, fontSize: 13, marginBottom: 10 }} />

      <div style={{ background: '#fff', border: `1px solid ${BR.border}`, borderRadius: 8, overflow: 'hidden' }}>
        {loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: BR.muted }}>Loading…</div>
        ) : filtered.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: BR.muted, fontSize: 13 }}>
            No anodize batches {filter !== 'ALL' ? `in ${filter} state` : 'yet'}. Click "Record Batch" to start.
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead>
              <tr style={{ background: BR.bg, borderBottom: `1px solid ${BR.border}` }}>
                {['Status','Batch #','Process','Tank','Bath','Seal','Test Panel','Started','Operator','QA','WO',''].map(h => (
                  <th key={h} style={{ padding: '9px 10px', textAlign: 'left', fontSize: 10,
                                        fontWeight: 800, color: BR.muted, textTransform: 'uppercase', letterSpacing: 0.4 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map(i => {
                const info = STATUS_INFO[i.status] || STATUS_INFO.RUNNING;
                const pc = PANEL_COLOR[i.test_panel_result] || BR.muted;
                return (
                  <tr key={i.id} style={{ borderBottom: `1px solid ${BR.border}` }}>
                    <td style={{ padding: '9px 10px' }}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4,
                                      padding: '2px 8px', borderRadius: 4, fontWeight: 800, fontSize: 10,
                                      background: info.bg, color: info.color, border: `1px solid ${info.color}` }}>
                        {info.icon} {info.label}
                      </span>
                    </td>
                    <td style={{ padding: '9px 10px', fontFamily: 'monospace', fontWeight: 800 }}>{i.batch_no}</td>
                    <td style={{ padding: '9px 10px', maxWidth: 160 }}>
                      <div style={{ fontWeight: 700 }}>{i.process_type || '—'}</div>
                      <div style={{ fontSize: 10, color: BR.muted }}>{i.spec || ''}</div>
                    </td>
                    <td style={{ padding: '9px 10px', fontFamily: 'monospace' }}>{i.tank_id || '—'}</td>
                    <td style={{ padding: '9px 10px', fontSize: 11, color: BR.muted }}>
                      {i.bath_temp_c ? `${i.bath_temp_c}°C` : '—'}{i.voltage_v ? ` · ${i.voltage_v}V` : ''}{i.immersion_min ? ` · ${i.immersion_min}m` : ''}
                    </td>
                    <td style={{ padding: '9px 10px', fontSize: 11 }}>{i.seal_type || '—'}</td>
                    <td style={{ padding: '9px 10px' }}>
                      <div style={{ fontFamily: 'monospace', fontSize: 11 }}>{i.test_panel_no || '—'}</div>
                      <span style={{ fontSize: 10, fontWeight: 800, color: pc }}>{i.test_panel_result || 'PENDING'}</span>
                    </td>
                    <td style={{ padding: '9px 10px', fontSize: 11 }}>{fmtDate(i.start_ts)}</td>
                    <td style={{ padding: '9px 10px' }}>{i.operator_name || '—'}</td>
                    <td style={{ padding: '9px 10px' }}>
                      {i.qa_signoff_by ? (
                        <span style={{ fontSize: 11, fontWeight: 700, color: BR.green }}>✓ {i.qa_signoff_by}</span>
                      ) : (
                        <button onClick={() => signOff(i)}
                                style={{ padding: '3px 8px', background: BR.orange, color: '#fff',
                                          border: 'none', borderRadius: 4, fontSize: 10, fontWeight: 800, cursor: 'pointer' }}>
                          Sign off
                        </button>
                      )}
                    </td>
                    <td style={{ padding: '9px 10px' }}>
                      {(i.wo_numbers || []).slice(0, 2).map(wo => (
                        <Chip key={wo} label={wo} size="small"
                              sx={{ height: 18, fontSize: 9, fontWeight: 700, fontFamily: 'monospace',
                                     background: '#eff6ff', color: '#1e3a8a', border: '1px solid #93c5fd', marginRight: 0.3, marginBottom: 0.3 }} />
                      ))}
                      {(i.wo_numbers || []).length > 2 && (
                        <span style={{ fontSize: 10, color: BR.muted }}>+{i.wo_numbers.length - 2}</span>
                      )}
                    </td>
                    <td style={{ padding: '9px 10px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                      <Tooltip title="Edit"><IconButton size="small" onClick={() => openEdit(i)}>
                        <EditIcon fontSize="small" sx={{ color: '#111' }} />
                      </IconButton></Tooltip>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <div style={{ marginTop: 12, fontSize: 11, color: BR.muted, padding: '8px 12px',
                     background: '#fff7ed', border: `1px solid #fed7aa`, borderRadius: 6 }}>
        <strong style={{ color: '#9a3412' }}>AC7108 note:</strong> Every anodize load runs with a <strong>test specimen (coupon panel)</strong> processed alongside the parts. The panel is destructively tested (coating weight, seal quality / dye-stain, thickness) to prove the tank load conformed — the finished part itself cannot be inspected for this. A batch may not be accepted until its test panel is <strong>PASS</strong>. Bath temperature, voltage and immersion time are the controlled parameters; tank chemistry titration and rectifier calibration recency feed from the Calibration Register.
      </div>

      {/* Edit dialog */}
      <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: 800 }}>
          {items.some(i => i.id === draft.id) ? `Edit anodize batch` : 'Record new anodize batch'}
        </DialogTitle>
        <DialogContent dividers>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
            <TextField label="Batch # *" size="small" fullWidth
                       value={draft.batch_no}
                       onChange={e => setDraft({ ...draft, batch_no: e.target.value })}
                       placeholder="ANO-2026-0042" />
            <TextField label="Process Type *" size="small" fullWidth select
                       value={draft.process_type}
                       onChange={e => setDraft({ ...draft, process_type: e.target.value })}>
              <MenuItem value="">— Select —</MenuItem>
              {PROCESS_TYPES.map(p => <MenuItem key={p} value={p}>{p}</MenuItem>)}
            </TextField>
            <TextField label="Spec / Class" size="small" fullWidth
                       value={draft.spec}
                       onChange={e => setDraft({ ...draft, spec: e.target.value })}
                       placeholder="MIL-A-8625F Type II Cl.1" />
            <TextField label="Tank" size="small" fullWidth select
                       value={draft.tank_id}
                       onChange={e => setDraft({ ...draft, tank_id: e.target.value })}>
              <MenuItem value="">— Select —</MenuItem>
              {TANKS.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
            </TextField>
            <TextField label="WO Numbers (comma-sep)" size="small" fullWidth
                       value={(draft.wo_numbers || []).join(', ')}
                       onChange={e => setDraft({ ...draft, wo_numbers: e.target.value.split(',').map(s => s.trim()).filter(Boolean) })} />
            <TextField label="Qty Parts" size="small" fullWidth type="number"
                       value={draft.qty_parts}
                       onChange={e => setDraft({ ...draft, qty_parts: e.target.value })} />
            <TextField label="Bath Temp (°C)" size="small" fullWidth type="number"
                       value={draft.bath_temp_c}
                       onChange={e => setDraft({ ...draft, bath_temp_c: e.target.value })} />
            <TextField label="Voltage (V)" size="small" fullWidth type="number"
                       value={draft.voltage_v}
                       onChange={e => setDraft({ ...draft, voltage_v: e.target.value })} />
            <TextField label="Current Density (ASF)" size="small" fullWidth type="number"
                       value={draft.current_density_asf}
                       onChange={e => setDraft({ ...draft, current_density_asf: e.target.value })} />
            <TextField label="Immersion (min)" size="small" fullWidth type="number"
                       value={draft.immersion_min}
                       onChange={e => setDraft({ ...draft, immersion_min: e.target.value })} />
            <TextField label="Seal Type" size="small" fullWidth select
                       value={draft.seal_type}
                       onChange={e => setDraft({ ...draft, seal_type: e.target.value })}>
              <MenuItem value="">— Select —</MenuItem>
              {SEALS.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
            </TextField>
            <TextField label="Coating Weight (mg/dm²)" size="small" fullWidth type="number"
                       value={draft.coating_weight_mgdm2}
                       onChange={e => setDraft({ ...draft, coating_weight_mgdm2: e.target.value })} />
            <TextField label="Test Panel #" size="small" fullWidth
                       value={draft.test_panel_no}
                       onChange={e => setDraft({ ...draft, test_panel_no: e.target.value })}
                       placeholder="PANEL-0042" />
            <TextField label="Test Panel Result" size="small" fullWidth select
                       value={draft.test_panel_result}
                       onChange={e => setDraft({ ...draft, test_panel_result: e.target.value })}>
              {PANEL_RESULTS.map(r => <MenuItem key={r} value={r}>{r}</MenuItem>)}
            </TextField>
            <TextField label="Operator" size="small" fullWidth
                       value={draft.operator_name}
                       onChange={e => setDraft({ ...draft, operator_name: e.target.value })} />
            <TextField label="Start Time" size="small" fullWidth type="datetime-local"
                       value={draft.start_ts ? draft.start_ts.slice(0, 16) : ''}
                       InputLabelProps={{ shrink: true }}
                       onChange={e => setDraft({ ...draft, start_ts: e.target.value })} />
            <TextField label="End Time" size="small" fullWidth type="datetime-local"
                       value={draft.end_ts ? draft.end_ts.slice(0, 16) : ''}
                       InputLabelProps={{ shrink: true }}
                       onChange={e => setDraft({ ...draft, end_ts: e.target.value })} />
            <TextField label="Status" size="small" fullWidth select
                       value={draft.status}
                       onChange={e => setDraft({ ...draft, status: e.target.value })}>
              <MenuItem value="RUNNING">▶ Running</MenuItem>
              <MenuItem value="COMPLETE_OK">✓ Accepted</MenuItem>
              <MenuItem value="COMPLETE_HOLD">⚠ On hold</MenuItem>
              <MenuItem value="REJECTED">✗ Rejected</MenuItem>
            </TextField>
            <TextField label="Notes" size="small" fullWidth multiline minRows={2}
                       value={draft.notes}
                       onChange={e => setDraft({ ...draft, notes: e.target.value })}
                       sx={{ gridColumn: '1 / span 3' }} />
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)}>Cancel</Button>
          <Button onClick={save} disabled={saving} variant="contained"
                  sx={{ background: BR.orange, '&:hover': { background: '#ea580c' } }}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
